import axios from 'axios'
import VueAxios from 'vue-axios'
import { toast } from 'vue3-toastify'
import qs from 'qs'
import settings from '@/settings'

axios.defaults.baseURL = settings.API_URL

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const DEVICE_UNIQUE_ID_COOKIE = 'deviceUniqueId'
const DEVICE_UNIQUE_ID_HEADER = 'DeviceUniqueId'
const DEVICE_UNIQUE_ID_API_PATHS = [
  'user/login',
  'public/user/reset-password',
  'public/user/confirm-email',
  'public/user/verify-login-otp',
  'public/user/verify-signup-otp',
  'user/public/user/reset-password',
  'user/public/user/confirm-email',
  'user/public/user/verify-login-otp',
  'user/public/user/verify-signup-otp'
]

export const addDeviceUniqueIdHeader = (config, app) => {
  const shouldAddDeviceUniqueIdHeader = () => {
    return DEVICE_UNIQUE_ID_API_PATHS.some((path) => config.url.includes(path))
  }

  if (!shouldAddDeviceUniqueIdHeader()) return config

  const headers = config.headers || {}
  const { $cookies, $filters } = app.config.globalProperties
  let deviceUniqueId = $cookies.get(DEVICE_UNIQUE_ID_COOKIE)

  if (!deviceUniqueId) {
    deviceUniqueId = $filters.generateUUID()
    $cookies.set(DEVICE_UNIQUE_ID_COOKIE, deviceUniqueId, Infinity)
  }

  headers[DEVICE_UNIQUE_ID_HEADER] = deviceUniqueId

  config.headers = headers
  return config
}

export function initAxios({ app, router, i18n }) {
  const axios_include_timezone = axios.create()

  const axiosRequestHandler = (config) => {
    const { $cookies } = app.config.globalProperties
    const token = $cookies.get('token')

    if (token && !config.url.includes('https://abanprime.fsn1.your-objectstorage.com')) {
      config.headers['Authorization'] = 'Bearer ' + token
    }

    if (!config.headers['X-Debug-Request-Id']) {
      config.headers['X-Debug-Request-Id'] = generateRequestId()
    }

    return addDeviceUniqueIdHeader(config, app)
  }

  const axiosErrorHandler = (error) => {
    const t = i18n.global.t
    const translateMessage = (message) => (message ? t(message) : message)

    if (error.name === 'AbortError' || axios.isCancel(error)) {
      console.log('Request was aborted. Ignoring error.')
      return Promise.reject(error)
    }

    const resp = error.response || null

    if (resp && resp.status === 429) {
      try {
        window.dispatchEvent(new CustomEvent('api-429'))
      } catch (e) {
        const evt = document.createEvent('Event')
        evt.initEvent('api-429', true, true)
        window.dispatchEvent(evt)
      }
      toast.error(t('Too many requests. Please try again later.'))
    }

    if (error.config.errorHandling === false) {
      // intentionally empty
    } else if (error.code === 'ERR_NETWORK') {
      const toastMsg = `<div class="flex flex-col !items-start">
                          <span class="font-bold text-base">${t('networkErrorToast')}</span>
                          <span>${t('connectionProblem')}</span>
                          <div>
                            <span>${t('tryLater')}</span>
                            <button class="text-primary" onclick="window.location.reload()">
                              ${t('reload')}
                            </button>
                          </div>
                        </div>`
      toast(toastMsg, {
        autoClose: false,
        multiple: false,
        closeOnClick: false,
        dangerouslyHTMLString: true,
        theme: 'custom'
      })
    } else if (error.code === 'ECONNABORTED') {
      toast.error(t('Request Timeout!'))
    } else if (error.response?.status === 401) {
      const { $cookies } = app.config.globalProperties
      $cookies.remove('token')
      if (
        router.currentRoute.value.path !== '/' &&
        router.currentRoute.value.meta.needAuth !== false
      ) {
        router.push({ name: 'Login' })
      }
    } else if (error.response?.status >= 500 && error.response?.status < 600) {
      toast.error(t('Server Error!'))
    } else if (
      error.response?.data instanceof Object &&
      'code' in error.response.data &&
      'ignoreErrorCodes' in error.config &&
      error.config.ignoreErrorCodes.includes(error.response.data.code)
    ) {
      // intentionally empty
    } else if ([400, 402, 403, 404, 405, 406].includes(error.response?.status)) {
      if (error.response?.data?.message) {
        toast.error(translateMessage(error.response.data.message), {
          position: toast.POSITION.TOP_CENTER,
          theme: toast.THEME.COLORED,
          toastId: error.response.data.message
        })
      } else {
        toast.error(t('Bad Request!'))
      }
    }

    return Promise.reject(error)
  }

  axios.interceptors.response.use((response) => response, axiosErrorHandler)
  axios.interceptors.request.use(axiosRequestHandler)

  axios.defaults.paramsSerializer = (params) => {
    return qs.stringify(app.config.globalProperties.$filters.removeNullAndEmptyValues(params), {
      encode: false,
      allowDots: true
    })
  }

  axios_include_timezone.defaults.paramsSerializer = axios.defaults.paramsSerializer
  axios_include_timezone.defaults.baseURL = axios.defaults.baseURL

  app.config.globalProperties.axios_include_timezone = axios_include_timezone

  axios_include_timezone.interceptors.request.use((config) => {
    const requestConfig = axiosRequestHandler(config)
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    requestConfig.headers['UserTimeZone'] = userTimeZone
    requestConfig.params = {
      ...requestConfig.params,
      UserTimeZone: userTimeZone
    }
    return requestConfig
  })

  axios_include_timezone.interceptors.response.use((response) => response, axiosErrorHandler)

  app.use(VueAxios, axios)
  app.use(VueAxios, axios_include_timezone)

  return { axios, axios_include_timezone }
}
